'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';

const navLinks = [
{ label: 'Home', href: '/', isAnchor: false },
{ label: 'About Us', href: '/about-us', isAnchor: false },
{ label: 'Products', href: '/products', isAnchor: false },
{ label: 'Media Room', href: '/media-room', isAnchor: false },
{ label: 'Career', href: '/career', isAnchor: false },
{ label: 'Contact Us', href: '/contact-us', isAnchor: false }];

const productMenu = [
{ label: 'All Products', href: '/products' },
{ label: 'Chocolate Chhatua', href: '/products/chocolate-chhatua' },
{ label: 'Mix Chhatua', href: '/products/mix-chhatua' },
{ label: 'Sugar-Free Chhatua', href: '/products/sugar-free-chhatua' },
{ label: 'Chana Sattu', href: '/products/chana-sattu' },
{ label: 'Chakki Atta', href: '/products/chakki-atta' },
{ label: 'Ragi Flour', href: '/products/ragi-flour' }];

const adminLink = { label: 'Admin', href: '/admin' };


const socialLinks = [
{ label: 'Facebook', href: 'https://www.facebook.com/kasturichhatua', icon: 'facebook' },
{ label: 'Instagram', href: 'https://www.instagram.com/kasturifoodproducts/', icon: 'instagram' },
{ label: 'LinkedIn', href: 'https://www.linkedin.com/company/kasturi-foods/', icon: 'linkedin' },
{ label: 'X', href: 'https://twitter.com/Kasturi_Foods', icon: 'x' },
{ label: 'YouTube', href: 'https://www.youtube.com/channel/UC5JnFdZjwrI3Rj2zrB6i0DA', icon: 'youtube' }];


function SocialIcon({ type }: {type: string;}) {
  if (type === 'facebook') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>);

  if (type === 'instagram') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>);

  if (type === 'x') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>);

  if (type === 'youtube') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" />
    </svg>);

  if (type === 'linkedin') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45z" />
    </svg>);

  return null;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  // On non-home pages, always show the opaque header style
  const isHomePage = pathname === '/';
  const showOpaque = !isHomePage || scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on scroll
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('scroll', close, { passive: true });
    return () => window.removeEventListener('scroll', close);
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showOpaque ?
        'py-3 shadow-lg' :
        'py-4'}`
        }
        style={{
          background: showOpaque ? 'rgba(253, 248, 242, 0.96)' : 'transparent',
          backdropFilter: showOpaque ? 'blur(12px)' : 'none',
          borderBottom: showOpaque ? '1px solid var(--border)' : 'none'
        }}>

        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0" aria-label="Kasturi Food Products Home">
            <AppImage
              src="/assets/images/kasturi_logo.png"
              alt="Kasturi Food Products logo — India's clean-label superfood brand"
              width={220}
              height={104}
              priority
              className="h-14 w-auto object-contain"
              style={{ filter: showOpaque ? 'none' : 'brightness(0) invert(1)' }} />

          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navLinks.map((link) => {
              const isActive = link.href === '/' ? pathname === '/' : pathname.startsWith(link.href);
              const activeStyle = isActive
                ? { color: showOpaque ? 'var(--foreground)' : 'white', borderBottom: '2px solid var(--accent)', paddingBottom: '2px' }
                : { color: showOpaque ? 'var(--muted-foreground)' : 'rgba(255,255,255,0.85)', borderBottom: '2px solid transparent', paddingBottom: '2px' };

              if (link.label === 'Products') {
                return (
                  <div key={link.label} className="relative group">
                    <Link
                      href={link.href}
                      className="block text-sm font-medium transition-colors duration-200"
                      style={activeStyle}>
                      {link.label}
                    </Link>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200">
                      <ul className="min-w-[210px] rounded-xl py-2 shadow-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                        {productMenu.map((item) => (
                          <li key={item.href}>
                            <Link href={item.href} className="block px-5 py-2 text-sm hover:bg-[var(--secondary)] transition-colors" style={{ color: 'var(--foreground)' }}>
                              {item.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              }

              return link.isAnchor ? (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium transition-colors duration-200"
                  style={activeStyle}
                  onMouseEnter={(e) => { if (!isActive) (e.target as HTMLElement).style.color = showOpaque ? 'var(--foreground)' : 'white'; }}
                  onMouseLeave={(e) => { if (!isActive) (e.target as HTMLElement).style.color = showOpaque ? 'var(--muted-foreground)' : 'rgba(255,255,255,0.85)'; }}>
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium transition-colors duration-200"
                  style={activeStyle}
                  onMouseEnter={(e) => { if (!isActive) (e.target as HTMLElement).style.color = showOpaque ? 'var(--foreground)' : 'white'; }}
                  onMouseLeave={(e) => { if (!isActive) (e.target as HTMLElement).style.color = showOpaque ? 'var(--muted-foreground)' : 'rgba(255,255,255,0.85)'; }}>
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right: Social + CTA */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-3">
              {socialLinks.map((s) =>
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="transition-colors duration-200"
                style={{ color: showOpaque ? 'var(--muted-foreground)' : 'rgba(255,255,255,0.7)' }}
                onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.color = showOpaque ? 'var(--foreground)' : 'white';}}
                onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.color = showOpaque ? 'var(--muted-foreground)' : 'rgba(255,255,255,0.7)';}}>

                  <SocialIcon type={s.icon} />
                </a>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg transition-colors"
            style={{ color: showOpaque ? 'var(--foreground)' : 'white' }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}>

            {menuOpen ?
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg> :

            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            }
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 flex flex-col justify-center items-center md:hidden ${
        menuOpen ? 'opacity-100 pointer-events-all' : 'opacity-0 pointer-events-none'}`
        }
        style={{ background: 'rgba(42,16,21,0.97)', backdropFilter: 'blur(12px)' }}>

        <nav className="flex flex-col items-center gap-8 text-center" aria-label="Mobile navigation">
          {navLinks.map((link) => {
            const isActive = link.href === '/' ? pathname === '/' : pathname.startsWith(link.href);
            return link.isAnchor ? (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="font-display font-[200] uppercase tracking-wide transition-opacity"
                style={{ fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', color: isActive ? 'var(--accent)' : 'white', opacity: isActive ? 1 : undefined }}>
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="font-display font-[200] uppercase tracking-wide transition-opacity"
                style={{ fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', color: isActive ? 'var(--accent)' : 'white', opacity: isActive ? 1 : undefined }}>
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-6 mt-12">
          {socialLinks.map((s) =>
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            className="text-white/60 hover:text-white transition-colors">

              <SocialIcon type={s.icon} />
            </a>
          )}
        </div>
      </div>
    </>
  );

}